// 人気ゲームランキングの「プレイ率」＝**全ゲームの総プレイ回数に占めるシェア**を出す
// （2026-08-21・ユーザー判断。回数そのものは画面に出さない）。
//
// 🔴 **素直に四捨五入すると合計が 100% にならない**。5本で 28/16/12/8/4 = 68回なら
//   41.2 / 23.5 / 17.6 / 11.8 / 5.9 → 41+24+18+12+6 = **101%**。
//   「全体に占める割合」と名乗る表が足して 101% になるのは、並べて見える表では目に付く。
//   ⇒ **最大剰余方式**（各値を切り捨てて、余りを小数部の大きい順に1ずつ配る）で
//     **合計をちょうど 100 に揃える**。
//
// ⚠️ **合計を 100 に揃えてよいのは、渡した行が総回数を覆っているときだけ**。
//   `limit` で上位 N 本に切られている場合（10本構想で本数が limit を超えたとき）は
//   残りのゲームのぶんが欠けているので、揃えると**嘘になる**＝そのときは素の四捨五入に落とす。
//
// ⚠️ **0回のゲームを 1% に押し上げないこと**。左外部結合で 0回の新作も並ぶので、
//   余りの配り先は**回数が 1 以上の行だけ**に限る。

/**
 * プレイ回数の配列 → 表示用の整数パーセントの配列（同じ並び）。
 *
 * @param plays 各行のプレイ回数（サーバーの並び順のまま）
 * @param total **全ゲームの**総プレイ回数（`limit` の外側で数えた分母）
 */
export function sharePercents(plays: readonly number[], total: number): number[] {
  const safeTotal = Number.isFinite(total) && total > 0 ? total : 0;
  if (safeTotal === 0) return plays.map(() => 0);

  const exact = plays.map((n) => (Number.isFinite(n) && n > 0 ? (n / safeTotal) * 100 : 0));
  const out = exact.map((v) => Math.floor(v));

  // 渡された行だけで総回数を覆っているか（覆っていなければ揃えない）。
  const covered = plays.reduce((a, n) => a + (Number.isFinite(n) && n > 0 ? n : 0), 0) === safeTotal;
  if (!covered) return exact.map((v) => Math.round(v));

  let rest = 100 - out.reduce((a, v) => a + v, 0);
  if (rest <= 0) return out;

  // 🔴 **他に遊ばれているゲームがあるのに「100%」を出さない**。
  //   99.68% は最大剰余で 100 へ切り上がるが、その下に「1%未満」の行が並ぶと
  //   **100% が「他は0回」と読めて矛盾する**（実データで踏んだ: 10000 対 30 対 1 対 1 対 0）。
  //   ⇒ 100 に届く行を1つ飛ばして次の候補へ配る。飛ばした先も1以上の回数を持つので、
  //     **合計 100 は保ったまま**「99% / 1% / 1%未満…」という読める並びになる。
  //   ※ 本当に1本しか遊ばれていないときは、その1本が 100% で正しい（飛ばさない）。
  const soleGame = plays.filter((n) => Number.isFinite(n) && n > 0).length <= 1;

  // 小数部が大きい順に1ずつ。同点は元の並び（＝回数の多い順）を優先する。
  const order = exact
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .filter((e) => e.frac > 0 && plays[e.i]! > 0)
    .sort((a, b) => b.frac - a.frac || a.i - b.i);

  for (const e of order) {
    if (rest === 0) break;
    if (!soleGame && out[e.i]! + 1 >= 100) continue;
    out[e.i]! += 1;
    rest -= 1;
  }
  return out;
}
